/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { ClipboardList, Plus } from "lucide-react";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { DataTable, NameCell, type Column, type TableSort } from "../components/ui/DataTable";
import { DirectoryCard } from "../components/ui/DirectoryCard";
import { PageHeader } from "../components/ui/PageHeader";
import { Select } from "../components/ui/Select";
import { Tooltip } from "../components/ui/Tooltip";
import { FilterBar } from "../components/FilterBar";
import { RegistryDirectory, type TableList } from "../components/RegistryDirectory";
import { useListView, ViewToggle } from "../components/ViewToggle";
import { listReferrals, reminderDays, type ReferralListItem } from "../api/referrals";
import { useChrome } from "../contexts/ChromeContext";
import { formatMmmDdYyyy, formatTimeAgo } from "../lib/date-utils";
import {
  filtersFromParams,
  filtersToScim,
  paramsWithFilters,
  type ActiveFilter,
} from "../lib/filters";
import {
  REFERRAL_SORTS,
  commsLabel,
  nextStep,
  referralFilterFields,
  SOURCE_LABELS,
  STAGE_LABELS,
  stageDetail,
  stageTone,
} from "../lib/referrals";
import { cameFrom } from "../lib/navigation";
import { headerSort, NEWEST, paramsWithSort, type SortChoice } from "../lib/sorting";


function familyName(referral: ReferralListItem): string {
  return referral.clientNames.length > 0 ? referral.clientNames.join(", ") : "Unnamed family";
}

function referredBy(referral: ReferralListItem): string {
  return [referral.organizationName ?? "Unknown organization", referral.providerName]
    .filter(Boolean)
    .join(" · ");
}

function receivedOn(iso: string | null): string {
  return iso ? formatMmmDdYyyy(iso) : "—";
}

function Stage({ referral }: { referral: ReferralListItem }) {
  const line = stageDetail(referral);
  return (
    <span className="flex min-w-0 flex-col items-start gap-1">
      <Badge tone={stageTone(referral.stage)}>{STAGE_LABELS[referral.stage]}</Badge>
      {line && (
        <Tooltip content={line.text} className="max-w-full">
          <span
            className={
              line.alert
                ? "block truncate text-xs font-medium text-warning"
                : "block truncate text-xs text-text-tertiary"
            }
          >
            {line.text}
          </span>
        </Tooltip>
      )}
    </span>
  );
}

const COLUMNS: Column<ReferralListItem>[] = [
  {
    key: "family",
    header: "Family",
    cell: (referral) => <NameCell to={`/referrals/${referral.id}`}>{familyName(referral)}</NameCell>,
    width: 140,
    minWidth: 120,
    pinned: true,
  },
  {
    key: "provider",
    header: "Provider",
    cell: (referral) => <span className="block truncate">{referral.providerName ?? "—"}</span>,
    sortKey: "provider",
    width: 122,
  },
  {
    key: "organization",
    header: "Organization",
    cell: (referral) => <span className="block truncate">{referral.organizationName ?? "—"}</span>,
    sortKey: "organization",
    width: 152,
  },
  {
    key: "source",
    header: "Source",
    cell: (referral) => <span className="whitespace-nowrap">{SOURCE_LABELS[referral.source]}</span>,
    hidden: true,
    width: 128,
  },
  {
    key: "createdAt",
    header: "Received",
    cell: (referral) => <span className="whitespace-nowrap">{receivedOn(referral.createdAt)}</span>,
    sortKey: "createdAt",
    defaultOrder: "desc",
    width: 124,
  },
  {
    key: "stage",
    header: "Stage",
    cell: (referral) => <Stage referral={referral} />,
    sortKey: "stage",
    width: 232,
    minWidth: 140,
  },
  {
    key: "comms",
    header: "Comms",
    cell: (referral) => <span className="block truncate">{referral.comms ? commsLabel(referral.comms) : "—"}</span>,
    width: 168,
    minWidth: 128,
  },
  {
    key: "nextStep",
    header: "Next step",
    cell: (referral) => <span className="block truncate">{nextStep(referral.onboarding) ?? "—"}</span>,
    hidden: true,
    width: 180,
  },
  {
    key: "lastTouchpointAt",
    header: "Last touchpoint",
    cell: (referral) => <span className="whitespace-nowrap">{formatTimeAgo(referral.lastTouchpointAt)}</span>,
    sortKey: "lastTouchpointAt",
    defaultOrder: "desc",
    width: 164,
  },
];

/**
 * The ordering a sort key and direction name: one of the menu's choices, or any sortable column
 * in either direction, which the table header offers without the menu having to list it.
 */
function choiceFor(key: string, order: SortChoice["sortOrder"]): SortChoice {
  const known = REFERRAL_SORTS.find((choice) => choice.sortBy === key && choice.sortOrder === order);
  const column = COLUMNS.find((candidate) => candidate.sortKey === key);
  return known ?? (column ? headerSort(key, order, column.header) : NEWEST);
}

function sortFromParam(value: string): SortChoice {
  const [key = "", order] = value.split(":");
  return order === "asc" || order === "desc" ? choiceFor(key, order) : NEWEST;
}

/** Every family referred to Manatee, with how far their onboarding got. */
export function Referrals() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useListView("referrals", "table");
  const [days, setDays] = useState<number[]>([]);
  useEffect(() => {
    void reminderDays().then((result) => {
      if (result.ok) {
        setDays(result.value.days);
      }
    });
  }, []);
  const fields = referralFilterFields(days, searchParams.get("comms"));
  const filters = filtersFromParams(fields, searchParams);
  const setFilters = (next: ActiveFilter[]) =>
    setSearchParams(
      (previous) => paramsWithFilters(previous, fields, next),
      { replace: true },
    );
  const filter = filtersToScim(fields, filters);
  const sort = sortFromParam(searchParams.get("sort") ?? "");
  const setSort = (choice: SortChoice) =>
    setSearchParams((previous) => paramsWithSort(previous, choice.value), { replace: true });
  const onTableSort = (next: TableSort) => setSort(choiceFor(next.key, next.order));
  useChrome({}, []);

  const table = ({ items, ...paging }: TableList<ReferralListItem>) => (
    <DataTable
      {...paging}
      caption="Referrals"
      columns={COLUMNS}
      rows={items}
      rowKey={(referral) => referral.id}
      rowHref={(referral) => `/referrals/${referral.id}`}
      sort={{ key: sort.sortBy, order: sort.sortOrder }}
      onSort={onTableSort}
      storageKey="referrals"
    />
  );

  return (
    <div className="space-y-8">
      <PageHeader
        title="Referrals"
        action={
          <Button variant="secondary" size="md" onClick={() => void navigate("/referrals/new", { state: cameFrom(location) })}>
            <Plus size={16} />
            New referral
          </Button>
        }
      />

      <RegistryDirectory
        countNoun="referral"
        countHint="Referrals recorded since the referral system launched (Oct 2025). Earlier referrals are not in this list."
        placeholder="Search by family, provider, email, or phone"
        load={(q, page) => listReferrals(q, page, filter, sort)}
        filterKey={`${filter ?? ""}|${sort.value}`}
        controls={
          view === "cards" && (
            <Select
              className="w-full sm:w-48"
              value={sort.value}
              options={REFERRAL_SORTS.includes(sort) ? REFERRAL_SORTS : [...REFERRAL_SORTS, sort]}
              onChange={(value) => setSort(sortFromParam(value))}
              ariaLabel="Sort referrals"
            />
          )
        }
        filters={
          <FilterBar
            fields={fields}
            active={filters}
            onChange={setFilters}
            beside={<ViewToggle value={view} onChange={setView} />}
          />
        }
        view={view}
        table={table}
        itemKey={(referral) => referral.id}
        renderCard={(referral) => <ReferralCard key={referral.id} referral={referral} />}
        empty={{
          icon: <ClipboardList size={20} />,
          title: "No referrals match",
          description: "Try another family, provider, email, or phone, or clear the filters.",
        }}
      />
    </div>
  );
}

function ReferralCard({ referral }: { referral: ReferralListItem }) {
  const { onboarding } = referral;
  return (
    <DirectoryCard
      to={`/referrals/${referral.id}`}
      title={familyName(referral)}
      subtitle={referredBy(referral)}
      footer={
        <span className="flex min-w-0 gap-1">
          {referral.comms && <span className="truncate">{commsLabel(referral.comms)} ·</span>}
          <span className="shrink-0 whitespace-nowrap">{formatTimeAgo(referral.lastTouchpointAt)}</span>
        </span>
      }
      footerEnd={`Referral ${referral.id}`}
    >
      <div className="relative z-10 flex flex-wrap items-start gap-2">
        <Stage referral={referral} />
        {onboarding?.consultOnly && <Badge tone="info">Consult only</Badge>}
      </div>
    </DirectoryCard>
  );
}
